import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Plus, Edit2, ExternalLink, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminPagesList() {
    const [pages, setPages] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get('/admin/pages');
            setPages(data);
        } catch (e) {
            toast.error('Greška pri učitavanju stranica');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const onDelete = async (id) => {
        if (!confirm('Obrisati stranicu?')) return;
        try {
            await api.delete(`/admin/pages/${id}`);
            toast.success('Stranica obrisana');
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Brisanje nije uspjelo');
        }
    };

    return (
        <div>
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-medium">Stranice</h1>
                    <p className="text-sm text-white/60 mt-1">Sve stranice na sajtu – uredite naslov, sadržaj, sekcije i SEO.</p>
                </div>
                <Button asChild className="bg-[#B08D57] hover:bg-[#9c7d4e] text-white" data-testid="admin-pages-new">
                    <Link to="/admin/pages/new"><Plus className="mr-2 h-4 w-4" /> Nova stranica</Link>
                </Button>
            </div>

            <Card className="mt-6 bg-[#111318] border-[#232A36] overflow-hidden">
                <table className="w-full text-sm" data-testid="admin-table">
                    <thead className="text-white/60 text-xs uppercase tracking-wider">
                        <tr className="border-b border-[#232A36]">
                            <th className="text-left px-4 py-3">Naslov</th>
                            <th className="text-left px-4 py-3">Slug</th>
                            <th className="text-left px-4 py-3">Tip</th>
                            <th className="text-left px-4 py-3">Status</th>
                            <th className="text-right px-4 py-3">Akcije</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan="5" className="px-4 py-8 text-center text-white/60">Učitavanje…</td></tr>
                        ) : pages.length === 0 ? (
                            <tr><td colSpan="5" className="px-4 py-8 text-center text-white/60">Nema stranica.</td></tr>
                        ) : pages.map((p) => (
                            <tr key={p.id} className="border-b border-[#232A36] hover:bg-[#161A21]">
                                <td className="px-4 py-3 font-medium">{p.title}</td>
                                <td className="px-4 py-3 text-white/70 font-mono text-xs">/{p.slug === 'home' ? '' : p.slug}</td>
                                <td className="px-4 py-3 text-white/70">{p.page_type || 'custom'}</td>
                                <td className="px-4 py-3">
                                    <Badge variant="outline" className={p.status === 'published' ? 'border-green-700 text-green-400' : 'border-yellow-700 text-yellow-400'}>
                                        {p.status === 'published' ? 'Objavljeno' : 'Skica'}
                                    </Badge>
                                </td>
                                <td className="px-4 py-3">
                                    <div className="flex items-center justify-end gap-2">
                                        <Button asChild size="sm" variant="outline" className="border-[#232A36] text-white hover:bg-[#232A36]">
                                            <a href={`/${p.slug === 'home' ? '' : p.slug}`} target="_blank" rel="noreferrer"><ExternalLink className="h-3.5 w-3.5" /></a>
                                        </Button>
                                        <Button asChild size="sm" variant="outline" className="border-[#232A36] text-white hover:bg-[#232A36]" data-testid={`edit-page-${p.slug}`}>
                                            <Link to={`/admin/pages/${p.id}`}><Edit2 className="h-3.5 w-3.5" /></Link>
                                        </Button>
                                        {!p.is_system && (
                                            <Button size="sm" variant="outline" onClick={() => onDelete(p.id)} className="border-red-900 text-red-400 hover:bg-red-900/20">
                                                <Trash2 className="h-3.5 w-3.5" />
                                            </Button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </Card>
        </div>
    );
}
